import hyperdom from 'hyperdom'
import windowEvents from 'hyperdom/windowEvents'
import formatDate from '../../../../../packages/dates/formatDate.js'
import formats from '../../../../../packages/dates/formats.js'
import formatPrice from '../../packages/ui-helpers/formatPrice.js'
import formatDepositPrice from '../../packages/ui-helpers/formatDepositPrice.js'
import sum from 'root-core/utils/array/sum.js'
import {flatten, groupBy} from 'lowscore'
import pluralize from 'pluralize'
import commissionEngine from '../../../../../packages/price-engine/commissions/commissionEngine.js'
import checkRoomAvailability from '../../checkRoomAvailability.js'
import RoomOptions from '../../roomOptions.jsx'
import roomTypes from '../../../../../packages/accommodation/roomTypes.js'
import tripNotes from '../../tripNotes.jsx'
import titlecase from 'title-case'
import * as gtmDataLayer from '../../analytics/gtm/dataLayer.js'
import responsiveRender from '../../responsiveRender.js'
import assetPath from '../../assetPath.js'
import {isApplicableSupplementEnabled, applicableSupplementSelector} from '../../../../../packages/price-engine/applicableSupplementReduction.js'
import breakpoints from '../../packages/ui-helpers/breakpoints.js'
import DiscountCode from '../discountCode.jsx'

export default class Summary {
  constructor ({
    user,
    config,
    api,
    priceBreakdown,
    departure,
    progress,
    total,
    totalBeforeDiscount,
    discounts,
    anyDiscounts,
    bookingCreated,
    quoteCriteria,
    allPromotions,
    showLoading,
    hideLoading,
    promotions,
    nextTab,
    showExtras,
    showPassengers,
    bookNow,
    showEmailQuote,
    clearErrors,
    showTripOnRequestModal,
    showRoomOnRequestModal,
    isExtrasAvailable,
    getCurrentTabId,
    modals,
  }) {
    this.user = user
    this.api = api
    this.priceBreakdown = priceBreakdown
    this.departure = departure
    this.progress = progress
    this.total = total
    this.totalBeforeDiscount = totalBeforeDiscount
    this.discounts = discounts
    this.anyDiscounts = anyDiscounts
    this.bookingCreated = bookingCreated
    this.quoteCriteria = quoteCriteria
    this.allPromotions = allPromotions
    this.config = config
    this.features = config.features
    this.showLoading = showLoading
    this.hideLoading = hideLoading
    this.promotions = promotions
    this.nextTab = nextTab
    this.showExtras = showExtras
    this.showPassengers = showPassengers
    this.bookNow = bookNow
    this.showEmailQuote = showEmailQuote
    this.clearErrors = clearErrors
    this.showTripOnRequestModal = showTripOnRequestModal
    this.showRoomOnRequestModal = showRoomOnRequestModal
    this.showExpandedSummary = false
    this.showTripNotesContent = false
    this.isExtrasAvailable = isExtrasAvailable
    this.getCurrentTabId = getCurrentTabId
    this.discountCode = new DiscountCode({
      config,
      quoteCriteria,
      showLoading,
      modals,
      promotions,
      bookingCreated,
      progress,
      hideLoading,
      clearErrors,
      departure,
      api,
      user,
    })

    this.roomOptions = new RoomOptions({
      binding: {
        get: () => quoteCriteria.selectedRoomAllocation || [],
        set: (rooms) => quoteCriteria.selectRoomAllocation(rooms),
      },
      quoteCriteria: quoteCriteria,
      currency: departure.prices.currency,
    })

    this.setOnTop = onTop => this.onTop = onTop
    this.tripNotes = (this.departure.notes || []).filter(n => n.departureNote || n.productNote || n.sellingCompanyNote)
    this.modals = modals
  }

  getFlightDetailsFromLeg (leg) {
    return {
      arrive: leg.lastFlight.arrive,
      cabin: leg.firstFlight.cabin,
      departDate: leg.firstFlight.departDate,
      departTime: leg.firstFlight.departDate,
      depart: leg.firstFlight.depart,
    }
  }

  getFlights ({priceBreakdown}) {
    let outboundFlight
    let returnFlight
    const {promisedFlights, flights} = priceBreakdown

    if (promisedFlights && promisedFlights.legs.length > 0) {
      const flights = priceBreakdown.promisedFlights.legs
      outboundFlight = flights.find(x => x.type === 'outbound')
      returnFlight = flights.find(x => x.type === 'inbound')
    } else if (flights && flights.legs && flights.legs.length > 0) {
      const {legs} = flights
      outboundFlight = this.getFlightDetailsFromLeg(legs[0])
      returnFlight = this.getFlightDetailsFromLeg(legs[legs.length - 1])
    }
    return {outboundFlight, returnFlight}
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  renderCTA ({isMobile}) {
    const goToPassengersTab = () => {
      this.closeExpandedSummary({isMobile})
      this.showPassengers()
    }

    const goToPaymentTab = () => {
      this.closeExpandedSummary({isMobile})
      this.bookNow()
    }

    const paymentContinueText = this.config.brandName === 'Contiki'
      ? <span className='booking-summary__cta-long' data-item-type='General Interaction' data-item-name='trip-summary-cta'>: Payment</span>
      : ''

    const ctaOnClick = callback => {
      this.closeExpandedSummary({isMobile})
      checkRoomAvailability({
        rooms: this.roomOptions.binding.get().rooms,
        roomsOccupancy: this.departure.roomsOccupancy,
        roomAllocations: this.quoteCriteria.roomAllocations,
        cbTripOnRequest: () => this.showTripOnRequestModal(),
        cbRoomOnRequest: () => this.showRoomOnRequestModal(),
        cbNextTab: callback,
      })
    }
    const tabId = this.progress.currentTab.id
    let ctaContent

    if (tabId === 'tour-overview') {
      ctaContent = [
        this.renderButtons({ctaOnClick}),
        this.renderRevealExtrasHyperlink(),
      ]
    }
    if (tabId === 'extras'){
      ctaContent = <button
        data-test='trip-summary-continue'
        className="btn-primary -full-width passengers-continue"
        type="submit"
        form="extrasForm"
        onclick={() => goToPassengersTab()}
        data-item-type='General Interaction'
        data-item-name='trip-summary-cta'
      >
        Continue<span className="booking-summary__cta-long">: Passenger Details</span>
      </button>
    }
    if (tabId === 'passengers') {
      ctaContent = <button
        data-test='trip-summary-continue'
        class='btn-primary -full-width payment-continue'
        type='submit'
        form='passengerDetailsForm'
        onclick={() => goToPaymentTab()}
        data-item-type='General Interaction'
        data-item-name='trip-summary-cta'
      >
        Continue{paymentContinueText}
      </button>
    }
    if (tabId === 'payment') { return }

    return <div className="booking-summary-bottom__cta">
      {ctaContent}
      {!this.config.hideFeesAndCharges &&
        <div className="booking-summary__cta-info">
          <span className="booking-summary__cta-info__content"></span>
        </div>}
    </div>
  }

  renderRedesignedBottomSummary () {

    const renderPricing = () => {
      const formatedPrice = formatPrice(this.total())
      const summaryAction = !this.showExpandedSummary ? 'close' : 'open'

      return <div className='booking-summary-bottom__pricing' onclick={() => this.toggleExpandedSummary()}>
        <span className='booking-summary-bottom__pricing-header'>Total</span>
        <span className='booking-summary-bottom__pricing-total'>{formatedPrice}</span>
        <i className={`booking-summary-bottom__pricing-icon ${summaryAction}`}></i>
        {this.renderSummaryDepositInfo()}
      </div>
    }

    return <div className="booking-summary-bottom">
      { this.renderYourTotalSavings() }
      { renderPricing() }
      { this.renderCTA({isMobile: true}) }
      { this.renderDepositProminence() }
    </div>
  }

  renderRedesignedSummary () {
    const priceBreakdown = this.priceBreakdown()
    const {outboundFlight, returnFlight} = this.getFlights({priceBreakdown})

    const resetOnResize = () => {
      const bookingSummaryElement = document.querySelector('.booking-summary')
      if (bookingSummaryElement && window.innerWidth >= breakpoints.desktop.startWidth) {
        bookingSummaryElement.style.transform = `translateY(0)`
        this.showExpandedSummary = false
      }
    }

    return <div
      id='summary'
      className={['booking-summary', {'booking-summary--mobile' : this.config.isMobile}]}
      data-test='trip-summary'
      attributes={{
        currenttab: this.getCurrentTabId(),
        componentversion: this.getComponentVersion(),
      }}
    >
      <h2 className='booking-summary__header'>Trip summary</h2>
      { this.renderTripNotes() }
      <div className='booking-summary__close-btn' onclick={() => this.closeExpandedSummary({isMobile: true})}></div>
      { this.renderSummaryDates({priceBreakdown, outboundFlight, returnFlight}) }
      { this.renderBreakdown() }
      { this.renderPromotions({priceBreakdown}) }
      { this.renderPrice() }
      { this.discountCode.renderDiscountBox({modal: false}) }
      { this.renderCommission() }
      { this.renderDepositProminence() }
      { this.renderCTA({isMobile: false}) }
      { windowEvents({
        onresize: () => resetOnResize(),
      }) }
    </div>
  }

  renderSummaryDates ({priceBreakdown, outboundFlight, returnFlight}) {
    return <div class='booking-summary__dates'>
      { this.renderOriginDeparture({isBeforeTrip: true}) }
      { this.renderFlights({isBeforeTrip: true, flightDetails: outboundFlight}) }
      { this.renderExtraStays({isBeforeTrip: true , priceBreakdown}) }
      { this.renderTripDetail({isBeforeTrip: true}) }
      { this.renderTripDetail({isBeforeTrip: false}) }
      { this.renderExtraStays({isBeforeTrip: false, priceBreakdown}) }
      { this.renderFlights({isBeforeTrip: false, flightDetails: returnFlight}) }
    </div>
  }

  renderOriginDeparture ({isBeforeTrip}) {
    const date = isBeforeTrip ? this.departure.startDate : this.departure.endDate
    const country = isBeforeTrip ? this.departure.startPlace.country : this.departure.endPlace.country
    const title = isBeforeTrip ? 'Depart' : ''

    return this.departure.originDepartureDate ? <div className="booking-summary__container">
      <div className="booking-summary__icon -new-ui_plane"></div>
      <div className="booking-summary__desc">
        <h4 className="booking-summary__title">{title}</h4>
        <p className="booking-summary__date booking-summary__date__start booking-summary__date__depart_origin_country">
          {this.progress.tourLoaded ? formatDate(date, formats.human) : ''}
        </p>
        <p className="booking-summary__location booking-summary__location__origin_country">Board overnight flight
          to {country} (not included).</p>
      </div>
    </div> : undefined
  }
  renderFlights ({isBeforeTrip, flightDetails}) {
    const title = isBeforeTrip ? 'Outbound Flight' : 'Return Flight'
    const className = isBeforeTrip ? 'outbound-flight' : 'return-flight'

    return flightDetails ?
      <div className={`booking-summary__container booking-summary__departure booking-summary__${className}`}>
        <div className="booking-summary__icon -new-ui_plane"></div>
        <div className="booking-summary__desc">
          <div className='booking-summary__title'>{title}</div>
          <p className='booking-summary__date booking-summary__date__depart_origin_country'>
            {formatDate(flightDetails.departDate, formats.human)}
          </p>
          <p className='booking-summary__location booking-summary__location__origin_country'>
            {`${flightDetails.depart} → ${flightDetails.arrive}`}
            {flightDetails.departTime ? ` at ${flightDetails.departTime.format('HH:mm')}` : undefined}
          </p>
        </div>
      </div> :
      undefined
  }

  renderExtraStays ({isBeforeTrip, priceBreakdown}) {
    const extraStay = isBeforeTrip ? priceBreakdown.extraStays.before : priceBreakdown.extraStays.after
    const title = isBeforeTrip ? 'Pre-trip accommodation' : 'Post-trip accommodation'
    const date = isBeforeTrip ? this.departure.startDate : this.departure.endDate
    const isTourLoaded = this.progress.tourLoaded
    const nights =  isBeforeTrip ? priceBreakdown.extraStays?.before?.nights : priceBreakdown.extraStays?.after?.nights
    const selectedHotel = isBeforeTrip ? priceBreakdown.extraStays?.before?.selectedHotel : priceBreakdown.extraStays?.after?.selectedHotel
    const hotelStartDate = formatDate(date.addDays(isBeforeTrip ? -nights : 0), formats.humanShort)
    const hotelEndDate = formatDate(date.addDays(isBeforeTrip ? 0 : nights), formats.humanShort)

    return extraStay ? <div className="booking-summary__container booking-summary__accommodation">
      <div className="booking-summary__icon -new-ui_hotel"></div>
      <div className={`booking-summary__desc booking-summary__accommodation-${isBeforeTrip ? 'before' : 'after'}`}>
        <div className='booking-summary__title'>{title}</div>
        <p className='booking-summary__date booking-summary__date__depart_origin_country'>
          {isTourLoaded ? hotelStartDate : ''}
          {' - '}
          {isTourLoaded ? hotelEndDate : ''}
        </p>
        <p className='booking-summary__location booking-summary__location__origin_country booking-summary__accommodation-before-hotels'>
          {pluralize('night', nights, true)+', '}
          <span style="text-transform: capitalize;">{
            roomTypes.displayNameForName(selectedHotel.preferredHotelRooms.rooms[0].roomType) +" Room, "}
          </span>
          {selectedHotel.name}
        </p>
      </div>
    </div>
      : undefined
  }

  renderTripDetail ({isBeforeTrip}) {
    const renderTripDetailDates = () => {
      return isBeforeTrip
        ? formatDate(this.departure.startDate, formats.human)
        : formatDate(this.departure.endDate, formats.human)
    }

    const renderTripDetailsPlace = () => {
      return isBeforeTrip
        ? `${this.departure.startPlace.city}, ${this.departure.startPlace.country}`
        : `${this.departure.endPlace.city}, ${this.departure.endPlace.country}`
    }
    const renderTripDetailTitle = () => {
      return isBeforeTrip
        ? 'Trip Start'
        : 'Trip End'
    }
    return <div className="booking-summary__container booking-summary__departure">
      <div className="booking-summary__icon -new-ui_calendar"></div>
      <div className="booking-summary__desc">
        <div className="booking-summary__title">{renderTripDetailTitle()}</div>
        <p className="booking-summary__date booking-summary__date__item booking-summary__date__depart_origin_country">
          {this.progress.tourLoaded ? renderTripDetailDates() : ''}
        </p>
        <p
          className="booking-summary__location booking-summary__location__start booking-summary__location__origin_country">
          {renderTripDetailsPlace()}
        </p>
      </div>
    </div>
  }


  renderPromotions ({priceBreakdown}) {
    const epd = (!this.quoteCriteria.discounts.epd || priceBreakdown.epdOverriddenByPromo) && priceBreakdown.availableEpd ? priceBreakdown.availableEpd : undefined
    const availablePromos = this.availableUnappliedPromos()
    const allUnapplied = availablePromos.concat(epd ? [epd] : [])

    let allUnappliedAppliable = this.quoteCriteria.promotions.find(p => p.automatic && !p.combinable)
      ? allUnapplied.filter(p => p.combinable)
      : allUnapplied

    allUnappliedAppliable = this.quoteCriteria.promotions.find(p => p.automatic && !p.epdCombinable)
      ? allUnappliedAppliable.filter(p => !p.epd)
      : allUnapplied

    if (this.showAvailablePromotions(allUnappliedAppliable)) {
      // TODO: what if these are for transfers or accomodation?
      allUnappliedAppliable.filter(p => !p.epd).forEach(p => p.amount = this.quoteCriteria.pricePromotion(p, priceBreakdown).amount)
      const promosThatCanBeViewed = !this.userCanViewDisplayablePromotionsForThisSellingCompany()
        ? allUnappliedAppliable.filter(p => p.epd || p.promotionSubtype === 'LMD')
        : allUnappliedAppliable
      const pricedPromos = promosThatCanBeViewed.sort((a, b) => {
        if (a.amount < b.amount) return 1
        if (a.amount > b.amount) return -1
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })

      return <div class='all-promotions'>
        <ul class='all-promotions__available'>
          {
            pricedPromos.map(p => (
              <li class='products-breakdown__add-promo'>
                <div class='products-breakdown__info'>
                  <div className='products-breakdown__headline'>
                    {p.name}
                    {(p.notes || p.paymentBy || p.depositPaymentBy) && (<div className="tooltip products-breakdown__tooltip">
                      <div className="tooltip__content">
                        <div
                          className="tooltip__header">{p.name}</div>
                        <div className="applied-promos__warning">
                          <span className="applied-promos__content">{this.discountCode.discountInfoMessages(p)}</span>
                        </div>
                        <div className="discount-notes__description">{p.notes}</div>
                      </div>
                    </div>)}
                  </div>
                  <div className='products-breakdown__price '>{
                    p.amount > 0
                      ? <span class='discount-code__price'>{ formatPrice({amount: p.amount, currency: priceBreakdown.currency, allowZero: true}) }</span>
                      : ''
                  }
                  <span className='applied-promos__add' onclick={() =>
                  {this.discountCode.addAvailablePromotion(p)}}> ADD
                  </span>
                  </div>
                </div>
              </li>
            ))
          }
        </ul>
      </div>
    }
  }

  render () {
    return <div>
      { this.renderRedesignedSummary() }
      { this.renderRedesignedBottomSummary() }
    </div>
  }

  renderTripNotes () {
    document.addEventListener('click', () => {
      this.showTripNotesContent = false
      this.refresh()
    })

    if (!this.shouldShowTripNotes()) { return }

    const toggleTripNotesContent = (ev, state) => {
      ev.preventDefault()
      ev.stopPropagation()
      this.showTripNotesContent = state
    }

    const renderTripNotesContent = () => {
      if (!this.showTripNotesContent) { return }

      return <div className="booking-summary__trip-notes--content" data-test='trip-summary-important-trip-notes'>
        <span className="trip-notes-content__title">Important Trip Notes</span>
        <div className="trip-notes-content__close-btn" onclick={ev => toggleTripNotesContent(ev, false)}></div>
        <div className="trip-notes-content__text">{tripNotes(this.tripNotes)}</div>
      </div>
    }

    return <div className='booking-summary__trip-notes'  onclick={ev => ev.stopPropagation()}>
      <span className='booking-summary__trip-notes--btn' onclick={ev => toggleTripNotesContent(ev, true)}>
        <span>Important Trip Notes</span>
        {renderTripNotesContent()}
      </span>
    </div>
  }

  renderRevealExtrasHyperlink () {
    const isSmallDevice = responsiveRender('large', () => true, () => false)
    if (isSmallDevice) return
    const {extrasEnabled, showExtrasHyperlink} = this.config.features
    if (extrasEnabled || !showExtrasHyperlink) return
    return <a
        id="revealExtrasHyperlink"
        onclick={() => this.revealExtras()}
        data-item-type='General Interaction'
        data-item-name='trip-summary-cta'
    >Add Extras</a>
  }

  revealExtras () {
    this.config.features.extrasEnabled = true
    this.nextTab()
  }

  renderWasPrice (price, prefix) {
    if (!price) return
    const allowPriceStrikeThrough = this.config.features.wasPriceStrikeThroughAllowed

    const reducedPriceLabelStyle = allowPriceStrikeThrough ? 'booking-summary__was-price-strikethrough' : 'booking-summary__was-price'
    return <div class='booking-summary__was'>
      <div class={reducedPriceLabelStyle}>
        { !allowPriceStrikeThrough ? <p>Standard Price</p> : undefined }
        <span>{prefix}{formatPrice(price)}</span>
      </div>
    </div>
  }

  renderPriceLabel () {
    const breakdown = this.priceBreakdown()
    if (breakdown.flights
      && this.config.flights
      && this.config.flights.pricing === 'bundled'
    ) {
      const totalWithoutFlights = formatPrice({
        currency: breakdown.currency,
        amount: breakdown.totalWithoutFlights,
      })
      return <div className="booking-summary__price-label-wrapper">
        <span className="booking-summary__price-label">Total</span>
        <div className="tooltip products-breakdown__tooltip boundled-flights__tooltip">
          <div className="tooltip__content">
            <span>Tour Price {totalWithoutFlights}</span>
            <span>Flights Included</span>
          </div>
        </div>
      </div>
    }
    return <span className="booking-summary__price-label">Total</span>
  }

  renderSupplementNotice (roomType) {
    const roomSupplement = applicableSupplementSelector({
      supplementAdjustments: this.departure.prices.roomSupplementAdjustments,
      roomType,
    })

    return roomSupplement.discountReduction && this.anyDiscounts() ?
      <div className="booking-summary__supplement-notice" style="margin-top: 1em">
        <span>Discounts are not applicable to the single room surcharge of this rate.</span>
      </div>
      : undefined
  }

  renderPrice (expandableSummaryMobile) {
    return <div className='booking-summary__price' data-test='booking-summary-price'>
      <div className='booking-summary__total' >
        {this.shouldShowTotalPriceWas() ? this.renderWasPrice(this.wasTotalBeforeDiscount()) : undefined}
        {this.renderPriceLabel()}
        <span className='booking-summary__initial-price'>{formatPrice(this.total())}</span>
      </div>
      {this.renderSummaryDepositInfo()}
      {this.renderAdvertisementMessage()}
      {this.renderYourTotalSavings()}
      <br/>
      {expandableSummaryMobile ? this.discountCode.renderDiscountBox({modal: true}) : undefined}
    </div>
  }

  renderSummaryDepositInfo () {
    const deposit = this.quoteCriteria.getDeposit()
    const depositAmount = deposit ? deposit.paymentAmount / this.quoteCriteria.passengers.length : undefined
    const mobileDepositProminenceEnabled = responsiveRender('large', () => this.config.features.mobileDepositProminenceEnabled)
    const alternativeDepositText = this.config.contikiExperiment && this.config.contikiExperiment.alternativeDepositText
    let depositText

    depositText = depositAmount && !mobileDepositProminenceEnabled
      ? <div class='booking-summary__deposit'>
        or pay today just
        <span class="booking-summary__deposit-bold"> {formatDepositPrice({
          currency: this.total().currency,
          amount: depositAmount,
        })} </span>
        <span>deposit per person</span>
      </div>
      : undefined
    depositText = alternativeDepositText
      ? <div class='booking-summary__deposit'>
        {
          <span  style={{whiteSpace: 'pre-line'}}>{this.config.contikiExperiment.alternativeDepositText}</span>
        }
      </div>
      : depositText
    return depositText
  }

  renderBreakdownAccommodation ({isBefore, breakdown, passengersCount}) {
    const hotel = isBefore ? breakdown.extraStays.before : breakdown.extraStays.after
    const title = `${isBefore ? 'Pre-accommodation' : 'Post-accommodation'} (${hotel?.nights})`

    return hotel ? <li className="products-breakdown__item products-breakdown__hotel">
      <div className={`products-breakdown__info products-breakdown__hotel__${isBefore ? 'before' : 'after'}`}>
        <div className="products-breakdown__headline">{title}</div>
        <div className="products-breakdown__price">{passengersCount} x {
          formatPrice({currency: breakdown.currency, amount: hotel.total / passengersCount})
        }</div>
      </div>
    </li> : undefined
  }

  renderBreakdownTransfers (breakdown) {
    const transfers = breakdown.transfers
    const renderTransfer = (transfer) => {
      const currency = this.priceBreakdown().currency
      return <div className={['products-breakdown__info', transfer.type]}>
        <div className="products-breakdown__detail">
          <h5 className="u-margin-none">{transfer.description}</h5>
        </div>
        <div className="products-breakdown__price">
          {transfer.total === 0
            ? 'FREE'
            : formatPrice({currency, amount: transfer.total})
          }
        </div>
      </div>
    }

    return transfers && transfers.length ?
      <li className="products-breakdown__item products-breakdown__transfers">
        {transfers.map(transfer => renderTransfer(transfer))}
      </li> : undefined
  }
  renderBreakdownIncludedProducts (breakdown) {
    const shouldBeDisplayed = this.showMiscellaneousProductBreakdown() && this.areMiscellaneousProductsAvailable(breakdown)
    const miscellaneousProducts = breakdown.aggregatedMiscellaneousProducts
    const displayMiscProdutsWitZeroValue = !this.config.hideZeroValueMiscProductsInSummary
    const productsToDisplay = miscellaneousProducts.filter(miscProduct => displayMiscProdutsWitZeroValue || !!miscProduct.totalPrice)
    const currency = breakdown.currency
    const renderMiscProducts = (miscProduct) => <div className="products-breakdown__info">
      <div className="products-breakdown__detail">{miscProduct.productName}</div>
      <div className="products-breakdown__price">
        {formatPrice({currency, amount: miscProduct.totalPrice})}
      </div>
    </div>

    return shouldBeDisplayed ?
      <li class="products-breakdown__item products-breakdown__mandatory_products">
        <h4 class="products-breakdown__headline">Included in your Tour</h4>
        {productsToDisplay.map(miscProduct => renderMiscProducts(miscProduct))}
      </li> : undefined
  }
  renderBreakdownAdditionalProducts (additionalProducts) {
    const currency = this.departure.prices.currency
    const renderAdditionalProduct = (additionalProduct) => <div className="products-breakdown__info">
      <div className="products-breakdown__detail">{additionalProduct.name}</div>
      <div className="products-breakdown__price">
        {formatPrice({currency, amount: additionalProduct.amount})}
      </div>
    </div>

    return additionalProducts.length ?
      <li className="products-breakdown__item products-breakdown__products">
        {additionalProducts.map(additionalProduct => renderAdditionalProduct(additionalProduct))}
      </li> : undefined
  }

  renderBreakdownDiscounts () {
    const discounts = this.discounts()
    const renderDiscount = (discount) => {
      const discountName = discount.name === 'Frequent Traveller Discount' ? this.config.localisedWords.ftd : discount.name
      const notes = discount.notes
      const currency = this.total().currency
      const renderRemoveDiscount = this.discountCanBeRemoved(discount)
        ? <span className="applied-promos__remove" onclick={() => {this.removeDiscount(discount)}}></span>
        : undefined

      const displayNotes = notes ? <div className="tooltip products-breakdown__tooltip">
        <div className="tooltip__content">
          <div className="tooltip__header">{discountName}</div>
          <div className="discount-notes__description">{notes}</div>
        </div>
      </div> : undefined

      return <div className="products-breakdown__info products-breakdown__saving">
        <div className="products-breakdown__headline" data-test={'trip-summary-discount-name'}>
          {discountName}{displayNotes}
        </div>
        <div data-test={'trip-summary-discount-value'} className="products-breakdown__price">
          - {formatPrice({amount: discount.amount, currency, allowZero: true})}
          {renderRemoveDiscount}
        </div>
        <span className="products-breakdown__discount-info-messages">{this.discountCode.discountInfoMessages(discount)}</span>
      </div>
    }

    return this.anyDiscounts() ?
      <li class="products-breakdown__item">
        {discounts.map(discount => renderDiscount(discount))}
      </li> : undefined
  }

  renderBreakdown () {
    const breakdown = this.priceBreakdown()
    const wasPriceBreakdown = this.wasPriceBreakdown()
    const additionalProducts = this.additionalProductsBreakdown(breakdown)
    const passengersCount = breakdown.passengers.length || 1
    this.addWasPriceToPassenger(breakdown, wasPriceBreakdown)

    return this.progress.criteriaLoaded ? <div class="cost-breakdown">
      <div class="cost-breakdown__body">
        <ul class="products-breakdown" data-test={'products-breakdown'}>
          {this.renderBreakdownHeader(breakdown)}
          {this.renderPassengersBreakdown(breakdown)}
          {this.renderBreakdownIncludedProducts(breakdown)}
          {this.renderBreakdownAccommodation({
            isBefore: true,
            breakdown,
            passengersCount,
          })}
          {this.renderBreakdownAccommodation({
            isBefore: false,
            breakdown,
            passengersCount,
          })}
          {this.renderFlightsBreakdown(breakdown)}
          {this.renderBreakdownTransfers(breakdown)}
          {this.renderBreakdownAdditionalProducts(additionalProducts)}
          {this.renderBreakdownDiscounts()}
          {this.renderAdvertisementMessage(true)}
        </ul>
      </div>
    </div> : undefined
  }

  renderRoomGroupTooltip (roomGroup, roomName, promisedFlightPrice, roomType) {
    return <div class="tooltip products-breakdown__tooltip" data-test={'products-breakdown-tooltip'}>
      <div class='tooltip__content'>
        {roomGroup.map((room, index) =>
          <div class='products-breakdown__room-details' data-test={'tooltip-products-breakdown-room-details'}>
            <div className='tooltip__header'>{roomName} Room {index + 1}</div>
            <p>{
              room.passengers.map(passenger => {
                return <div class='products-breakdown__info'>
                  <div class='products-breakdown__detail'>
                    Passenger {passenger.passengerId} ({passenger.type})
                  </div>
                  <div class='products-breakdown__price'>{formatPrice({
                    currency: this.total().currency,
                    amount: this.getPassengerRoomPrice(passenger) + promisedFlightPrice,
                  })
                  }</div>
                </div>
              })
            }</p>
          </div>
        )}
        {this.renderSupplementNotice(roomType)}
      </div>
    </div>
  }

  renderYourTotalSavings () {
    if (!this.shouldShowTotalPriceWas()) { return }

    const amount = this.wasTotalWithoutInsurance().amount - this.totalWithoutInsurance().amount
    const currency = this.total().currency
    const formatedPrice = formatPrice({amount, currency})

    return <div className='booking-summary__your-savings'>
      <span className='booking-summary__your-savings__label'>Your Total Saving</span>
      <div className="booking-summary__your-savings__amount">{formatedPrice}</div>
    </div>
  }

  renderAdvertisementMessage (isMobile) {
    return this.departure.advertisementMessage ?
      <div data-test='trip-summary-advertisement-message' className={['booking-summary__advertisement', 'booking-summary__advertisement-message', {'mobile': isMobile}]}>
        <img className='local_offer__icon' src={assetPath(`/public/img/local-offer.svg`)} alt='local-offer'/>
        <span className='booking-summary__advertisement__content'>{this.departure.advertisementMessage}</span>
      </div> : undefined
  }


  getPassengerRoomPrice (passenger) {
    return this.showMiscellaneousProductBreakdown()
      ? passenger.basicRoomPrice || 0
      : passenger.roomPrice || 0
  }

  groupPassengersByPrice (roomGroup, type, promisedFlightPrice) {
    const passengers = []
    roomGroup.forEach(room => {
      room.passengers.forEach(passenger => {
        if (passenger.type === type) {
          passengers.push(passenger)
        }
      })
    })
    return groupBy(passengers, (passenger) => this.getPassengerRoomPrice(passenger) + promisedFlightPrice)
  }

  renderPassengersBreakdown (breakdown) {
    const roomGroups = groupBy(breakdown.rooms, (room) => room.roomType)
    const promisedFlightPrice = breakdown.promisedFlights ? breakdown.promisedFlights.price || 0 : 0
    return Object.keys(roomGroups).map((roomGroupName) => {
      const roomGroup = roomGroups[roomGroupName]
      const roomName = titlecase(roomTypes.displayNameForName(roomGroupName))
      const adultsPriceGroups = this.groupPassengersByPrice(roomGroup, 'adult', promisedFlightPrice)
      const childrenPriceGroups = this.groupPassengersByPrice(roomGroup, 'child', promisedFlightPrice)

      return <li class='products-breakdown__item products-breakdown__room' data-test={'trip-summary-room'}>
        <div class='products-breakdown__info'>
          <div class='products-breakdown__headline' data-test={'trip-summary-room-type'}>
            {roomName} Room
            {this.renderRoomGroupTooltip(roomGroup, roomName, promisedFlightPrice, roomGroupName)}
          </div>
          <div class="products-breakdown__price-container" data-test={'trip-summary-room-price'}>
            {this.renderPassengersBreakdownPrice(adultsPriceGroups, promisedFlightPrice)}
            {this.renderPassengersBreakdownPrice(childrenPriceGroups, promisedFlightPrice)}
          </div>
        </div>
      </li>
    })
  }

  renderPassengersBreakdownPrice (passengerPriceGroups, promisedFlightPrice) {
    if (!passengerPriceGroups) return
    return Object.keys(passengerPriceGroups).reverse().map(price => {
      price = parseFloat(price)
      const priceGroup = passengerPriceGroups[price]
      const type = priceGroup[0].type
      const quantity = priceGroup.length
      const prefix = `${quantity} ${this.departure.childrenAllowed ? pluralize(type, quantity) : ''} x `
      const wasPrice = priceGroup[0].wasPrice + promisedFlightPrice
      const shouldDisplayWasPrice = this.shouldShowPassengerBreakdownPriceWas(price, wasPrice)

      return quantity ?
        <div className="products-breakdown__prices">
          { shouldDisplayWasPrice ?
            <div className="products-breakdown__was-price">
              {this.renderWasPrice({
                amount: wasPrice,
                currency: this.departure.prices.currency,
              }, prefix)}
            </div>
            : undefined
          }
          <div class="products-breakdown__price">
            {prefix}
            {formatPrice({
              amount: price,
              currency: this.departure.prices.currency,
            })}
          </div>
        </div>
        : undefined
    })
  }

  renderFlightsBreakdown (breakdown) {
    if (breakdown.flights) {
      const peopleCount = breakdown.passengers.length
      const price = this.config.flights.pricing === 'bundled'
        ? 'Included'
        : `${peopleCount} x ${formatPrice({
          currency: breakdown.currency,
          amount: breakdown.flights.total / peopleCount,
        })}`

      return <li class='products-breakdown__item products-breakdown__flights'>
        <div class="u-margin-bottom-small"><span>Flights</span></div>
        <div class='products-breakdown__info'>
          <div class='products-breakdown__detail'>
            {breakdown.flights.legs.map(leg => {
              return <div class='leg'>
                {leg.firstFlight.depart} → {leg.lastFlight.arrive}
              </div>
            })}
          </div>
          <div className='products-breakdown__price'>{price}</div>
        </div>
      </li>
    }
  }

  renderPartyDetails (breakdown) {
    let content

    if (this.quoteCriteria.noRoomsAvailable) {
      content = <span>No rooms available for your party</span>
    } else {
      const adultsCount = breakdown.passengers.filter(x => x.type === 'adult').length
      const childrenCount = breakdown.passengers.filter(x => x.type === 'child').length
      const partyAdults = `${adultsCount} ${pluralize((this.departure.childrenAllowed ? 'adult' : 'person'), adultsCount)}`
      const partyChildren = childrenCount ? `, ${childrenCount} ${pluralize(('child'), childrenCount)}` : ''
      content = partyAdults + partyChildren
    }

    return (
      <div class='products-breakdown__price'>
        <i className="-new-ui_passenger u-margin-right-tiny"></i>
        {content}
      </div>
    )
  }

  renderBreakdownHeader (breakdown) {
    return (
      <li class='products-breakdown__item products-breakdown__header'>
        <div class='products-breakdown__info-bold'>
          <div className='products-breakdown__headline'>Trip {breakdown.promisedFlights ? '+ International Flights' : undefined}</div>
          {this.renderPartyDetails(breakdown)}
        </div>
      </li>
    )
  }

  commission () {
    if (this.user.travelAgentCommission) {
      return commissionEngine({
        departure: this.departure,
        priceBreakdown: this.priceBreakdown(),
        commission: this.user.travelAgentCommission,
        features: this.features,
      })
    }
  }

  renderCommission () {
    if (!this.progress.criteriaLoaded || !this.commission()) { return }
    var self = this
    function toggleCommission () {
      self.showCommission = !self.showCommission
    }
    const commissionBreakdown = this.commission()
    const roomSupplement =  isApplicableSupplementEnabled({
      supplementAdjustments: this.departure.prices.roomSupplementAdjustments,
      rooms: commissionBreakdown.rooms,
    })
    return <div data-test={'trip-summary-agent-breakdown'} class='cost-breakdown cost-breakdown--commission show-sticky'>
      <div class='cost-breakdown__bar' onclick={toggleCommission}>
        {
          this.showCommission
            ? <div class='cost-breakdown__bar-close cost-breakdown__bar-toggle'>Hide agent breakdown</div>
            : <div class='cost-breakdown__bar-open cost-breakdown__bar-toggle'>Show agent breakdown</div>
        }
      </div>
      {
        (() => {
          if (this.showCommission) {
            const additionalProducts = this.additionalProductsBreakdown(commissionBreakdown)
            return <div>
              {
                this.features.taCommissionBreakdown ? <div class='cost-breakdown__body'>
                  <ul class='products-breakdown' data-test={'products-breakdown'}>
                    {
                      (() => {
                        const tourCommission = sum(flatten(commissionBreakdown.rooms.map(room => room.passengers.map(p => p.commission))))
                        return commissionBreakdown.totalCommission > 0 ? <li class='products-breakdown__item products-breakdown__tour'>
                          <h4 class='products-breakdown__headline'>Tour</h4>
                          <div class='products-breakdown__info'>
                            <div class='products-breakdown__detail'>
                              {this.departure.name}
                            </div>
                            <div class='products-breakdown__price'>{formatPrice({
                              currency: commissionBreakdown.currency,
                              amount: tourCommission,
                            })
                            }</div>
                          </div>
                        </li> : undefined
                      })()
                    }
                    {
                      (() => {
                        var aggregatedMiscellaneousProducts = commissionBreakdown.aggregatedMiscellaneousProducts
                        return aggregatedMiscellaneousProducts &&
                          aggregatedMiscellaneousProducts.length > 0 ?
                          <li class='products-breakdown__item products-breakdown__mandatory_products'>
                            <h4 class='products-breakdown__headline'>Included in your Tour</h4>
                            {
                              aggregatedMiscellaneousProducts.map(miscProduct => {
                                return <div class='products-breakdown__info'>
                                  <div class='products-breakdown__detail'>
                                    {miscProduct.productName}
                                  </div>
                                  <div class='products-breakdown__price'>{formatPrice({
                                    currency: commissionBreakdown.currency,
                                    amount: miscProduct.commission,
                                  })}</div>
                                </div>
                              })
                            }
                          </li>: undefined
                      })()
                    }
                    {
                      (() => {
                        const extrasStaysBeforeCommission = commissionBreakdown.extraStays.before ? commissionBreakdown.extraStays.before.commission : undefined
                        const extrasStaysAfterCommission = commissionBreakdown.extraStays.after ? commissionBreakdown.extraStays.after.commission : undefined
                        return extrasStaysBeforeCommission || extrasStaysAfterCommission ?
                          <li class='products-breakdown__item products-breakdown__hotel'>
                            <h4 class='products-breakdown__headline'>Extra Nights</h4>
                            {
                              extrasStaysBeforeCommission
                                ? <div class=' products-breakdown__info products-breakdown__hotel__before'>
                                  <div class='products-breakdown__detail'>
                                    <h5 class='u-margin-none'>{ commissionBreakdown.extraStays.before.selectedHotel.name}</h5>
                                      (Before trip, { pluralize('night', commissionBreakdown.extraStays.before.nights, true) })
                                  </div>
                                  <div class='products-breakdown__price'>{
                                    formatPrice({
                                      currency: commissionBreakdown.currency,
                                      amount: extrasStaysBeforeCommission,
                                    })
                                  }</div>
                                </div>
                                : undefined
                            }
                            {
                              extrasStaysAfterCommission
                                ? <div class='products-breakdown__info products-breakdown__hotel__after'>
                                  <div class='products-breakdown__detail'>
                                    <h5 class='u-margin-none'>{commissionBreakdown.extraStays.after.selectedHotel.name}</h5>
                                      (After trip, { pluralize('night', commissionBreakdown.extraStays.after.nights, true) })
                                  </div>
                                  <div class='products-breakdown__price'>{
                                    formatPrice({
                                      currency: commissionBreakdown.currency,
                                      amount: extrasStaysAfterCommission,
                                    })
                                  }</div>
                                </div>
                                : undefined
                            }
                          </li>
                          : undefined
                      })()
                    }
                    {
                      (() => {
                        if (commissionBreakdown.flights) {
                          return <li class='products-breakdown__item products-breakdown__flights'>
                            <h4>Flights</h4>
                            <div class='products-breakdown__info'>
                              <div class='products-breakdown__detail'>
                                {commissionBreakdown.flights.legs.map(leg => {
                                  return <div class='leg'>
                                    {leg.firstFlight.depart} → {leg.lastFlight.arrive}
                                  </div>
                                })}
                              </div>
                              <div class='products-breakdown__price'>{formatPrice({
                                currency: commissionBreakdown.currency,
                                amount: commissionBreakdown.flights.commission,
                              })}</div>
                            </div>
                          </li>
                        }
                      })()
                    }
                    {
                      commissionBreakdown.transfers && commissionBreakdown.transfers.length > 0
                        ? <li class='products-breakdown__item products-breakdown__transfers'>
                          <h4>Airport Transfers</h4>
                          { commissionBreakdown.transfers.map(transfer => {
                            return <div class={['products-breakdown__info', transfer.type]}>
                              <div class='products-breakdown__detail'>
                                <h5 class='u-margin-none'>{transfer.description}</h5>
                              </div>
                              <div class='products-breakdown__price'>{
                                formatPrice({
                                  currency: commissionBreakdown.currency,
                                  amount: transfer.commission,
                                })}</div>
                            </div>
                          })}
                        </li>
                        : undefined
                    }
                    {
                      additionalProducts.length > 0
                        ? <li class='products-breakdown__item products-breakdown__products'>
                          <h4 class='products-breakdown__headline'>Additional Products</h4>
                          {
                            additionalProducts
                              .map(additionalProduct => {
                                return <div class='products-breakdown__info'>
                                  <div class='products-breakdown__detail'>
                                    {additionalProduct.name}
                                  </div>
                                  <div class='products-breakdown__price'>
                                    {
                                      formatPrice({
                                        currency: commissionBreakdown.currency,
                                        amount: additionalProduct.commission,
                                      })
                                    }
                                  </div>
                                </div>
                              })
                          }
                        </li>
                        : undefined
                    }
                  </ul>
                </div>
                  : ''
              }

              <div class='cost-breakdown__body'>
                <div class='cost-breakdown__total'>
                  <span class='cost-breakdown__total-label'>Online Commission:</span>
                  <span class='cost-breakdown__total-amount'>{formatPrice({amount: commissionBreakdown.totalCommission, currency: commissionBreakdown.currency})}</span>
                  {
                    roomSupplement.commissionReduction
                      ? <span className="cost-breakdown__total-info">Please note that the single room surcharge of this rate is not commissionable.</span>
                      : undefined
                  }
                </div>
              </div>
            </div>
          }
        })()
      }
    </div>
  }

  availableUnappliedPromos () {
    if (this.user.isTravelAgent) {
      return this.allPromotions.filter(p => {
        return !p.automatic && !this.discounts().find(applied => applied.code === p.code)
      })
    } else {
      return this.allPromotions.filter(p => p.promotionSubtype === 'LMD' && !this.discounts().find(applied => applied.code === p.code))
    }
  }

  showAvailablePromotions (availablePromos) {
    const anyEPD = availablePromos.find(p => p.epd)
    const anyLMD = availablePromos.find(p => p.promotionSubtype === 'LMD')
    return this.discountCode.promotionsMayBeAdded() &&
      availablePromos.length > 0 &&
      (this.userCanViewDisplayablePromotionsForThisSellingCompany() || anyEPD || anyLMD)
  }

  visibleAvailablePromotionCount () {
    const availablePromos = this.availableUnappliedPromos()
    return this.showAvailablePromotions(availablePromos) && availablePromos.length
  }

  renderPromoNotes (promo, cssClass) {
    return <div class='tooltip -info promo-notes__tooltip'>
      <div class={`tooltip__content ${cssClass}`}>
        <h4 class='u-margin-top-none'>{promo.code} - {promo.name}</h4>
        <p class='discount-notes__description'>{promo.notes}</p>
        {
          promo.paymentBy || promo.depositPaymentBy
            ? <p>{this.discountCode.discountInfoMessages(promo)}</p>
            : undefined
        }
      </div>
    </div>
  }

  discountCanBeRemoved (discount) {
    return discount.discountType !== 'frequentTravellerDiscount' &&
      !discount.automatic &&
      !this.bookingCreated()
  }

  showCombinabiltyMessges () {
    return !this.bookingCreated()
  }

  priceReduced () {
    return this.total().amount !== this.totalBeforeDiscount().amount
  }

  shouldShowTotalPriceWas () {
    return this.config.features.showWasPrice && this.totalBeforeDiscount().amount < this.wasTotalBeforeDiscount().amount
  }

  shouldShowPassengerBreakdownPriceWas (baseRoomPrice, wasPrice) {
    return this.config.features.showWasPrice && baseRoomPrice && wasPrice && baseRoomPrice < wasPrice
  }

  shouldShowTripNotes () {
    return this.tripNotes.length > 0
  }

  removeDiscount (discount) {
    if (discount.promotionSubtype === 'LMD') {
      this.quoteCriteria.lmdRemoved = true
    }
    if(discount.promotionSubtype === 'ADV') {
      this.removeReferralAdvocate()
    }
    const discountIsPromotion = discount.discountType == 'promotion'
    if(discountIsPromotion){
      this.quoteCriteria.removeDiscount(discount.discountType, discount.code)
      this.promotionsLimitReached = this.config.features.restrictPromotions && this.quoteCriteria.promotions.length > 1
    } else {
      this.quoteCriteria.removeDiscount(discount.discountType, null)
    }
    this.discountCode.clearPromoErrors()
  }

  areMiscellaneousProductsAvailable (breakdown) {
    return Array.isArray(breakdown.aggregatedMiscellaneousProducts) && breakdown.aggregatedMiscellaneousProducts.length > 0
  }

  showMiscellaneousProductBreakdown () {
    return this.features.taMiscellaneousProductBreakdown
  }

  additionalProductsBreakdown (breakdown) {
    return breakdown.requestedAdditionalProducts
  }

  selectedTravelProtectionPlans () {
    var selectedProtections = []
    this.quoteCriteria.passengers.forEach(passenger => {
      if (passenger.travelProtection) {
        selectedProtections.push(passenger.travelProtectionPlan)
      }
    })
    return selectedProtections
  }

  totalTravelProtectionPrice () {
    return this.selectedTravelProtectionPlans().reduce((a, b) => { return a + b.price }, 0)
  }

  async getReferralAdvocate () {
    this.showLoading()
    const data = await this.api.getReferralAdvocate(
      this.config.sellingCompanyCode,
      this.departure.productCode,
      this.departure.code,
      this.quoteCriteria.passengers[0].emailAddress,
      this.quoteCriteria.referral.advocateId
    )
    if(data.promotion) {
      data.promotion.name = 'Referral Advocate Savings'
    }
    this.quoteCriteria.referral.availableCredits = data.advocate.availableCredits
    this.quoteCriteria.referral.advocateMessage = data.advocate.messageCode !== '0006' ? data.advocate.message : undefined
    this.hideLoading()
    return data
  }

  applyReferralAdvocate (advocate, promotion, passengerNumber = 0) {
    if(advocate.availableCredits > 0) {
      if(promotion) {
        this.removeReferralAdvocate()
        const breakdown = this.priceBreakdown()
        const maxAmount = breakdown.passengers[passengerNumber].total
        promotion.fixedAmount = Math.min(advocate.availableCredits, maxAmount)
        this.quoteCriteria.passengers[passengerNumber].applyAdvocatePromo = true
        this.discountCode.addAvailablePromotion(promotion)
        if(!this.quoteCriteria.referral) {
          this.quoteCriteria.referral = {}
        }

        this.quoteCriteria.referral.creditsToRedeem = promotion.fixedAmount
      }
    }
  }

  clearFtd () {
    this.quoteCriteria.passengers.forEach(passenger => {
      passenger.frequentTravellerDiscount = {selected: false}
      passenger.pastGuestBenefit = {selected: false}
    })
  }

  removeReferralAdvocate () {
    this.quoteCriteria.passengers.forEach(passenger => passenger.applyAdvocatePromo = false )
  }



  userCanViewDisplayablePromotionsForThisSellingCompany () {
    if (this.user && this.user.role === 'consumer' && this.user.whitelabel) {
      return false
    }

    if (this.user && this.user.role === 'travelAgent') {
      return this.config.features && this.config.features.showDisplayablePromotions
    }
    return false
  }



  toggleExpandedSummary () {
    const summary = document.querySelector('.booking-summary')
    const summaryBottom = document.querySelector('.booking-summary-bottom')
    const body = document.querySelector('body')
    const summaryViewHeight = this.config.isMobile
      ? this.getTransformationViewHeight(body.offsetHeight)
      : '70vh'

    if(!this.showExpandedSummary) {
      summary.style.transform = `translate(0px, calc(-${summaryViewHeight} - ${summaryBottom.offsetHeight}px))`
      body.style.overflow = 'hidden'
    } else {
      summary.style.transform = `translate(0px, 0px)`
      setTimeout(() => body.style.overflowY = 'scroll', 500) // wait 500ms for the .booking-summary transition-duration to complete
    }

    this.showExpandedSummary = !this.showExpandedSummary
  }

  closeExpandedSummary ({isMobile}) {
    if (!isMobile) { return }

    const parent = document.querySelector('.booking-summary')
    const body = document.querySelector('body')
    if(!parent || !body) { return }

    parent.style.transform = `translate(0px, ${this.getTransformationViewHeight(body.offsetHeight)})`
    setTimeout(() => body.style.overflowY = 'scroll', 500) // wait 500ms for the .booking-summary transition-duration to complete
    this.showExpandedSummary = false
  }

  getTransformationViewHeight (offsetHeight) {
    if(offsetHeight <= 650) {
      return '47vh'
    } else if(offsetHeight < 800) {
      return '57vh'
    } else {
      return '67vh'
    }
  }

  wasPriceBreakdown () {
    return this.quoteCriteria.wasPriceBreakdown()
  }

  wasTotalWithoutInsurance () {
    const breakdown = this.wasPriceBreakdown()
    return {
      amount: breakdown.totalBeforeDiscount - breakdown.productsExcludedFromDiscountsPrice,
      currency: breakdown.currency,
    }
  }

  totalWithoutInsurance () {
    const breakdown = this.quoteCriteria.priceBreakdown()
    return {
      amount: breakdown.total - breakdown.productsExcludedFromDiscountsPrice,
      currency: breakdown.currency,
    }
  }

  wasTotalBeforeDiscount () {
    var breakdown = this.wasPriceBreakdown() || {}
    return {
      amount: breakdown.totalBeforeDiscount,
      currency: breakdown.currency,
    }
  }

  addWasPriceToPassenger (breakdown, wasPriceBreakdown) {
    if (!breakdown || !wasPriceBreakdown) return
    breakdown.rooms.forEach((room, roomIndex) => {
      room.passengers.forEach((passenger, passengerIndex) => {
        const wasPrice = this.getPassengerRoomPrice(wasPriceBreakdown.rooms[roomIndex].passengers[passengerIndex])
        passenger.wasPrice = wasPrice
      })
    })
  }

  getComponentVersion () {
    const wasPriceEnabled = this.shouldShowTotalPriceWas()
    const mobileDepositProminenceEnabled = this.config.features.mobileDepositProminenceEnabled

    if (mobileDepositProminenceEnabled) {
      return 'deposit-prominence'
    }

    if (wasPriceEnabled) {
      return 'was-price'
    }

  }

  renderButtons ({ctaOnClick}) {
    const nextTabButton = (isColorReversed) => <button
      data-test='trip-summary-continue'
      data-item-type='General Interaction'
      data-item-name='trip-summary-cta'
      className={[
        `btn-primary${isColorReversed ? '--reversed-colors' : ''}`,
        '-full-width',
        'next-tab-continue',
        this.isExtrasAvailable() ? 'extras-continue': 'passengers-continue',
      ]} onclick={() => ctaOnClick(() => this.nextTab())}>
      Continue<span
        className='booking-summary__cta-long'
        data-item-type='General Interaction'
        data-item-name='trip-summary-cta'
    >{`: ${this.isExtrasAvailable() ? 'Add Extras' : 'Passenger Details'}`}</span>
    </button>
    return <div className="buttons">
      {nextTabButton(false)}
    </div>
  }

  renderDepositProminence () {
    const deposit = this.quoteCriteria.getDeposit()
    const depositAmount = deposit ? deposit.paymentAmount / this.quoteCriteria.passengers.length : 0
    this.shouldRenderDepositProminence = !!depositAmount
    if (!this.shouldRenderDepositProminence || !this.config.features.mobileDepositProminenceEnabled) return
    const depositAmountFormatted = formatDepositPrice({currency: this.total().currency, amount: depositAmount})

    return <div className="booking-summary__deposit-prominence">
      <i className="booking-summary__deposit-prominence__card"/>
      <span className="booking-summary__deposit-prominence__text">Book now for just {depositAmountFormatted}</span>
    </div>
  }
}
